import { createRouter, createWebHistory } from 'vue-router'
import store from './store'
import { getUxFlowRoute } from './flows/uxFlows'

//Components
const Home = () => import('./components/Home.vue')
const Login = () => import('./components/Login.vue')
const Invitation = () => import('./components/Invitation.vue')
const Activities = () => import('./components/Activities.vue')
const Activity = () => import('./components/Activity.vue')
const Students = () => import('./components/Students.vue')
const Teachers = () => import('./components/Teachers.vue')
const Locations = () => import('./components/Locations.vue')
const Student = () => import('./components/Student.vue')
const Devices = () => import('./components/Devices.vue')
const Schools = () => import('./components/Schools.vue')
const Games = () => import('./components/Games.vue')
const Generate = () => import('./components/Generate.vue')
const Settings = () => import('./components/Settings.vue')
const Dashboard = () => import('./components/Dashboard.vue')
const Leaderboards = () => import('./components/Leaderboards.vue')
const MultiView = () => import('./components/MultiView.vue')
const BsoFlow = () => import('./flows/bso/BsoFlow.vue')

//Route mapping
const routes = [
    { path: '/', component: Home },
    { path: '/login', component: Login },
    { path: '/invitation', component: Invitation },
    // Personal invitation from a colleague
    { path: '/invitation/:token', component: Invitation },
    // Older school links from the Klanten page; these register without a school
    { path: '/invitation/:schoolId/:legacyId', component: Invitation },
    { path: '/activities/:hideMenu', component: Activities },
    { path: '/activity/:studentId/:studentName/:activityId', component: Activity },
    { path: '/students', component: Students },
    { 
      path: '/teachers', 
      component: Teachers,
      beforeEnter: (to, from, next) => {
        if (store.state.user?.admin || store.state.user?.manageUsers) {
          next();
        } else {
          next('/dashboard');
        }
      }
    },
    {
      path: '/locations',
      component: Locations,
      beforeEnter: (to, from, next) => {
        if (store.state.user?.admin || store.state.user?.manageLocations) {
          next();
        } else {
          next('/dashboard');
        }
      }
    },
    { path: '/student/:studentId', component: Student },
    { path: '/devices', component: Devices },
    { 
      path: '/clients', 
      component: Schools,
      beforeEnter: (to, from, next) => {
        if (store.state.user?.admin) {
          next();
        } else {
          next('/dashboard');
        }
      }
    },
    {
      path: '/challenges',
      component: Games,
      beforeEnter: (to, from, next) => {
        if (store.state.user?.admin) {
          next();
        } else {
          next('/dashboard');
        }
      }
    },
    {
      path: '/generate',
      component: Generate,
      beforeEnter: (to, from, next) => {
        if (store.state.user?.admin || store.state.user?.contentCreator) {
          next();
        } else {
          next('/dashboard');
        }
      }
    },
    // Backwards-compatible redirect for old /schools bookmarks (the page is now called Klanten)
    { path: '/schools', redirect: '/clients' },
    // Backwards-compatible redirect for old /games links (e.g. bookmarks, AI-generated links)
    { path: '/games', redirect: (to) => ({ path: '/challenges', hash: to.hash }) },
    { path: '/settings', component: Settings },
    { path: '/dashboard', component: Dashboard },
    { path: '/leaderboards', component: Leaderboards },
    { path: '/multiview', component: MultiView },

    // Alternative UX flow, only for schools that have it selected (see the flow guard below)
    { path: '/flow/bso', component: BsoFlow, meta: { uxFlow: 'bso-beta' } },
  ]

export const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})

router.beforeEach((to, from, next) => {
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname)

  if (!isLocalHost && window.location.protocol !== 'https:') {
    window.location.href = 'https:' + window.location.href.substring(window.location.protocol.length)
    return
  }

  next()
})

// Where a logged-in user lands by default: the flow route of their school, or the dashboard.
export function homeRoute() {
  if (store.state.user?.admin) return '/dashboard'
  return getUxFlowRoute(store.state.uxFlow) || '/dashboard'
}

// Keep users of a school with an alternative UX flow inside that flow, and keep everyone
// else out of it. Admins may open every flow and the whole portal.
const ROUTES_OUTSIDE_FLOWS = ['/login', '/invitation']

router.beforeEach((to) => {
  if (!store.state.user || store.state.user.admin) return true
  if (ROUTES_OUTSIDE_FLOWS.some((path) => to.path.startsWith(path))) return true

  const flowRoute = getUxFlowRoute(store.state.uxFlow)
  if (flowRoute) {
    return to.path.startsWith(flowRoute) ? true : flowRoute
  }

  return to.meta.uxFlow ? '/dashboard' : true
})

const prefetchedRoutePaths = new Set()

export async function prefetchRoute(path) {
  if (!path || prefetchedRoutePaths.has(path)) return

  const resolved = router.resolve(path)
  const loaders = []

  for (const record of resolved.matched) {
    const lazyComponent = record.components?.default || record.component
    if (typeof lazyComponent === 'function') {
      loaders.push(lazyComponent().catch(() => null))
    }
  }

  await Promise.all(loaders)
  prefetchedRoutePaths.add(path)
}

export function warmUpRouteChunks(paths = []) {
  const uniquePaths = [...new Set(paths)].filter(Boolean)
  if (!uniquePaths.length) return

  const runPrefetch = () => {
    for (const path of uniquePaths) {
      prefetchRoute(path)
    }
  }

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(runPrefetch)
  } else {
    window.setTimeout(runPrefetch, 250)
  }
}

// Catch chunk-related route loading errors after deployments.
// Reload once per target path to fetch the latest assets without risking loops.
router.onError((error, to) => {
  const message = String(error?.message || '').toLowerCase()
  const isDynamicImportLoadError =
    message.includes('dynamically imported module') ||
    message.includes('dynamic import') ||
    message.includes('error loading module') ||
    message.includes('failed to fetch') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror')

  if (!isDynamicImportLoadError) return

  const targetPath = to?.fullPath || window.location.pathname + window.location.search + window.location.hash
  const reloadKey = `chunk-reload:${targetPath}`

  if (sessionStorage.getItem(reloadKey)) return

  sessionStorage.setItem(reloadKey, '1')
  window.location.href = targetPath
})